// Document storage-backing — the ONE decider of whether a document an order
// LISTS has real storage behind it (SOR-375).
//
// An e-filing order carries its document list in two stores that only agree if
// something enforces it: `orders.type_data.documents[]` (the wizard state, no
// storage fields by design) and the `order_documents` rows (where `gcs_path`
// lives). A listed entry with no backing row is a phantom — it renders as a
// card, passes field validation, and at submit the envelope builders silently
// drop it (they filter on the resolved binary URL), so the filing leaves
// without a document the filer believes is attached. Every consumer that needs
// this verdict — server write reconcile, readiness predicate, submit guard,
// and the portal's per-card upload state — resolves it HERE, keyed by
// `fileName`, the same reconciliation key the upload/attach path already uses.
//
// "Backed" means the row carries a non-empty `gcs_path`. `gcs_path` is
// nullable by design (a row can exist before its bytes land), so row presence
// alone is not backing, and this module must never be "helped" by tightening
// the column instead.
//
// Pure functions — no I/O, no imports beside the shared validation types.
/** The rule name every storage-backing validation error carries. */
export const MISSING_STORAGE_BACKING_RULE = 'missing-storage-backing';
/**
 * The fileNames whose rows actually hold storage — a non-empty `gcs_path`.
 * Keyed by `fileName`, deduplicated, membership-tested in O(1).
 */
export function backedFileNames(rows) {
    const backed = new Set();
    for (const row of rows) {
        if (row.gcsPath)
            backed.add(row.fileName);
    }
    return backed;
}
/** The fileNames an order lists, in listed order (duplicates preserved). */
export function listedFileNames(documents) {
    return documents.map(doc => doc.fileName);
}
function asSet(backed) {
    return backed instanceof Set ? backed : new Set(backed);
}
/**
 * The listed fileNames with NO storage behind them, in listed order,
 * deduplicated — the exact set a caller must report, block on, or strip.
 */
export function unbackedListedFileNames(listed, backed) {
    const backedSet = asSet(backed);
    const unbacked = [];
    for (const name of listed) {
        if (!backedSet.has(name) && !unbacked.includes(name))
            unbacked.push(name);
    }
    return unbacked;
}
/**
 * The listed entries that survive a write-time reconcile: only those whose
 * `fileName` is storage-backed, in listed order, untouched otherwise. This is
 * the drop half of the write-path invariant — what a writer persists when it
 * refuses to let a phantom entry into `type_data.documents[]`.
 */
export function reconcileListedDocuments(documents, backed) {
    const backedSet = asSet(backed);
    return documents.filter(doc => backedSet.has(doc.fileName));
}
/**
 * One validator error per unbacked listed document, for the readiness
 * predicate. Fielded to the `documents` section so the completeness summary
 * stays consistent with `readyForReview` (a phantom document IS a documents
 * section fault, not a field the filer left blank).
 */
export function unbackedStorageErrors(unbacked) {
    return unbacked.map(fileName => ({
        field: 'documents',
        rule: MISSING_STORAGE_BACKING_RULE,
        message: `"${fileName}" is listed on the order but has no stored file behind it — ` +
            're-upload the document before requesting review.',
        source: 'ours',
    }));
}
